import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useSetUserData } from '@pkg/hooks/queryHooks/useUserData.ts';
import { type EidCategories, type OrganizationBranding, UserDataKey } from '@pkg/types';

import { callAddBranding, callGetBranding } from '@app/client/resources/organizations.ts';
import { prefetch2FA } from '@app/helpers';

export function useGetPartnerData(partnerKey?: string) {
    const { partnerKeyParam } = useParams();
    const key = partnerKey || partnerKeyParam || '';
    return useQuery({
        queryKey: ['org-data', partnerKeyParam],
        queryFn: () => callGetBranding({ key }),
        enabled: !!key?.length,
        staleTime: Infinity,
        select: (r) => {
            if (r?.ok) {
                return { ...r.value, key };
            }
        },
    });
}
export function useSetPartnerData() {
    return useMutation({
        mutationFn: ({ value, key }: { value: OrganizationBranding; key: string }) =>
            callAddBranding({ value, key, prefetch: prefetch2FA() }),
    });
}
export function useUserAddresses() {
    const queryClient = useQueryClient();
    const setUserData = useSetUserData(UserDataKey.PartnerAddresses);

    return useCallback(
        async (value: EidCategories[]) => {
            if (setUserData.isPending) return;
            const res = await setUserData.mutateAsync(value);
            await queryClient.invalidateQueries({ queryKey: ['user-data'] });
            return res;
        },
        [queryClient, setUserData],
    );
}
